import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiShield, FiDollarSign, FiClock, FiUsers, FiArrowRight, FiAward } = FiIcons;

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      id: 1,
      title: 'Global Bank Achieves  100% GDPR Compliance in 6 Weeks',
      client: 'Fortune 500 Financial Institution',
      industry: 'Financial Services',
      challenge: 'Needed to achieve GDPR compliance across 50+ countries with over 100TB of customer data',
      solution: 'Implemented Microsoft Purview with automated data discovery, classification, and retention policies',
      results: [
        { metric: 'Compliance Achievement', value: '100%', description: 'Full GDPR compliance achieved' },
        { metric: 'Cost Savings', value: '$2.3M', description: 'Avoided potential fines' },
        { metric: 'Implementation Time', value: '6 weeks', description: '75% faster than projected' },
        { metric: 'Data Classified', value: '100TB+', description: 'Automatically classified sensitive data' }
      ],
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
      testimonial: {
        quote: "YourPurviewPartner transformed our data governance from chaos to compliance in just 6 weeks. The ROI was immediate and the peace of mind is invaluable.",
        author: "Chief Data Officer",
        company: "Global Financial Institution"
      }
    },
    {
      id: 2,
      title: 'Healthcare Network Reduces Data Breach Risk by 95%',
      client: 'Multi-Hospital Healthcare Network',
      industry: 'Healthcare',
      challenge: 'Managing PHI across 15 hospitals with inconsistent data governance and high breach risk',
      solution: 'Deployed comprehensive Microsoft Purview solution with automated PHI detection and protection',
      results: [
        { metric: 'Risk Reduction', value: '95%', description: 'Decreased data breach risk' },
        { metric: 'PHI Protected', value: '50M+', description: 'Patient records secured' },
        { metric: 'Compliance Score', value: '98%', description: 'HIPAA compliance rating' },
        { metric: 'Audit Time', value: '80%', description: 'Reduction in audit preparation' }
      ],
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop',
      testimonial: {
        quote: "Our HIPAA compliance went from concerning to exceptional. The automated PHI detection alone saved us countless hours and potential violations.",
        author: "VP of Information Security",
        company: "Regional Healthcare Network"
      }
    },
    {
      id: 3,
      title: 'Manufacturing Giant Streamlines Global Data Governance',
      client: 'International Manufacturing Corporation',
      industry: 'Manufacturing',
      challenge: 'Inconsistent data governance across 40+ facilities in 20 countries, causing operational inefficiencies',
      solution: 'Unified data governance platform using Microsoft Purview with standardized policies and procedures',
      results: [
        { metric: 'Efficiency Gain', value: '60%', description: 'Faster data access and processing' },
        { metric: 'Cost Reduction', value: '$1.8M', description: 'Annual operational savings' },
        { metric: 'Global Coverage', value: '40+', description: 'Facilities standardized' },
        { metric: 'Data Quality', value: '90%+', description: 'Improvement in data accuracy' }
      ],
      image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=600&h=400&fit=crop',
      testimonial: {
        quote: "Finally, we have consistent data governance across all our global operations. The efficiency gains have been remarkable.",
        author: "Global IT Director",
        company: "International Manufacturing Corp"
      }
    }
  ];

  const stats = [
    { number: '500+', label: 'Successful Implementations' },
    { number: '$50M+', label: 'Client Savings Generated' },
    { number: '98%', label: 'Average Compliance Score' },
    { number: '24/7', label: 'Support & Monitoring' }
  ];

  const industries = [
    { name: 'Financial Services', count: '150+', icon: FiTrendingUp },
    { name: 'Healthcare', count: '120+', icon: FiShield },
    { name: 'Manufacturing', count: '100+', icon: FiUsers },
    { name: 'Government', count: '80+', icon: FiAward },
    { name: 'Technology', count: '50+', icon: FiDollarSign }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Real Results from Real Companies
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                See how leading organizations have transformed their data governance with Microsoft Purview and achieved measurable business outcomes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Success Stories That Speak for Themselves
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how our clients achieved remarkable results with Microsoft Purview implementations
            </p>
          </div>
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-lg shadow-xl overflow-hidden"
              >
                <div className="lg:flex">
                  <div className="lg:w-1/2">
                    <img src={study.image} alt={study.title} className="w-full h-64 lg:h-full object-cover" />
                  </div>
                  <div className="lg:w-1/2 p-8">
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {study.industry}
                      </span>
                      <span className="text-gray-500 text-sm">{study.client}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {study.title}
                    </h3>
                    <div className="space-y-4 mb-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                        <p className="text-gray-600">{study.challenge}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4>
                        <p className="text-gray-600">{study.solution}</p>
                      </div>
                    </div>
                    {/* Results Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="bg-gray-50 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-600 mb-1">{result.value}</div>
                          <div className="text-sm font-semibold text-gray-900 mb-1">{result.metric}</div>
                          <div className="text-xs text-gray-600">{result.description}</div>
                        </div>
                      ))}
                    </div>
                    {/* Testimonial */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-gray-700 italic mb-3">"{study.testimonial.quote}"</p>
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">{study.testimonial.author}</div>
                        <div className="text-gray-600">{study.testimonial.company}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expertise spans across multiple industries with deep understanding of sector-specific compliance requirements
            </p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg text-center hover:shadow-lg transition-shadow"
              >
                <SafeIcon icon={industry.icon} className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{industry.name}</h3>
                <p className="text-blue-600 font-bold">{industry.count} Projects</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Proven Success Formula
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The same methodology that delivered these results can work for your organization
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <SafeIcon icon={FiShield} className="h-16 w-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Assess & Plan</h3>
              <p className="text-gray-600">
                Comprehensive assessment of your current state and custom roadmap development tailored to your specific requirements and industry regulations.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <SafeIcon icon={FiUsers} className="h-16 w-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Implement & Train</h3>
              <p className="text-gray-600">
                Expert implementation of Microsoft Purview with comprehensive training programs to ensure your team can effectively manage the new governance framework.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <SafeIcon icon={FiTrendingUp} className="h-16 w-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Optimize & Support</h3>
              <p className="text-gray-600">
                Continuous monitoring, optimization, and support to ensure sustained success and maximum ROI from your Microsoft Purview investment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join the 500+ companies who have transformed their data governance with our proven methodology
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/purview-audit"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
            >
              Get Your Purview Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Discuss Your Project
            </Link>
          </div>
          <p className="text-sm mt-6 text-blue-200">
            ✓ Proven methodology ✓ Industry expertise ✓ Measurable results ✓ Ongoing support
          </p>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;